import type { ComponentProps } from "react";

import { cx } from "./cx";

export interface ProgressBarProps extends Omit<ComponentProps<"div">, "children"> {
  /** 0..1. Ignored when `indeterminate`. */
  fraction?: number;
  /**
   * For work whose length is not knowable -- the final claim chunk being scored, or a model
   * response with no content-length. The slide animates on `transform` rather than
   * `margin`: it is shown during the longest main-thread block in the app, and a bar laid
   * out on that thread freezes exactly when it is the only thing on screen saying "still
   * working". Asked not to be moved, a bar that cannot slide says "working" by filling.
   */
  indeterminate?: boolean;
  barId?: string;
}

/**
 * The track stays a base rule in `app.css`, not a utility, and that is the point of it: the
 * boot overlay's progress bar is static markup in `index.html` and is on screen before this
 * bundle exists. One definition, shared by the overlay and by this.
 */
export function ProgressBar({ fraction = 0, indeterminate, barId, className, ...rest }: ProgressBarProps) {
  return (
    <div className={cx("progress", className)} {...rest}>
      <div
        id={barId}
        className={
          indeterminate
            ? "w-fraction animate-wrapup-slide motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-50"
            : undefined
        }
        style={
          indeterminate
            ? undefined
            : { width: `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%` }
        }
      />
    </div>
  );
}
